import { Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { Client } from '../../../api/types';
import { ClientFormComponent } from './client-form.component';
import {
  SynButtonComponent,
  SynDialogComponent,
  SynIconComponent,
  SynInputComponent,
} from '@synergy-design-system/angular';
import { SavedToastComponent } from '../../components/saved-toast.component';

@Component({
  selector: 'app-edit-client',
  template: `
    <div class="flex-row gap-2 align-baseline margin-bottom-1">
      <syn-button
        [disabled]="!canEdit() || !client().enabled"
        (click)="onEditClient()"
        size="small"
      >
        <syn-icon [name]="editable() ? 'close_fill' : 'edit_fill'" slot="prefix" />
        @if (editable()) {
          <span i18n="@@cancel-editing">Cancel Editing</span>
        } @else {
          <span i18n="@@start-editing">Start Editing</span>
        }
      </syn-button>

      <syn-button
        [disabled]="!canEdit()"
        (click)="openDeleteDialog()"
        size="small"
        data-e2e="btn-delete-client"
      >
        <syn-icon name="delete_fill" slot="prefix" />
        <span i18n="@@delete-client">Delete Client</span>
      </syn-button>
    </div>

    <app-client-form [client]="client()" [editable]="editable()"
                     submitIcon="save"
                     submitText="Save Changes"
                     i18n-submitText="@@save-changes"
                     (submitClient)="onSave($event)" />

    <app-saved-toast #savedToast />

    <syn-dialog
      label="Delete Client"
      i18n-label="@@delete-client"
      #deleteDialog>
      <div>
        <p i18n="@@delete-client-warning">
          Do you want to <strong>permanently delete</strong> the following client?
        </p>
        <span>{{ this.client().clientId }}</span>
        <p i18n="@@delete-client-confirmation">
          Please enter the Client ID to confirm deletion.
        </p>
        <syn-input [(value)]="clientIdToDelete" data-e2e="input-delete-client-id" />
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeDeleteDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onDeleteClient()" [disabled]="!canDelete()" data-e2e="btn-confirm-delete">
          <span i18n="@@delete-client">Delete Client</span>
        </syn-button>
      </div>
    </syn-dialog>
  `,
  styles: [
    `
    `,
  ],
  imports: [
    ClientFormComponent,
    SynButtonComponent,
    SynIconComponent,
    SynDialogComponent,
    SynInputComponent,
    SavedToastComponent,
  ],
})
export class EditClientComponent {
  id = inject(ActivatedRoute).snapshot.params['id'];
  api = inject(ServicePortalApi);
  router = inject(Router);

  canEdit = input.required<boolean>();

  client = input.required<Client>();
  editable = signal(false);
  clientIdToDelete = signal('');
  canDelete = computed(() => this.clientIdToDelete() === this.client().clientId);

  deleteDialog = viewChild.required<SynDialogComponent>('deleteDialog');
  savedToast = viewChild.required<SavedToastComponent>('savedToast');

  // emit a changed event to reload the client secrets
  // this is needed for the case of a initialy public client getting changed to confidential
  changed = output<boolean>();

  onEditClient() {
    this.editable.set(!this.editable());
  }

  openDeleteDialog() {
    void this.deleteDialog().nativeElement.show();
  }

  closeDeleteDialog() {
    void this.deleteDialog().nativeElement.hide();
  }

  onDeleteClient() {
    this.api.deleteClient(this.id, () => {
      console.log('Client deleted', this.id);
      void this.router.navigate(['clients']);
    });
  }

  onSave(client: Client) {
    this.api.updateClient(this.id, client, () => {
      this.changed.emit(true);
      this.savedToast().toast();
    });
    this.editable.set(false);
  }
}
